/**
 * `numeric-draft.ts` — the domain half of a numeric text field.
 *
 * # Why this exists
 *
 * A numeric `<input>` has TWO values that are easy to conflate:
 *
 *  - the **draft** — the literal characters the user has typed so far. It is
 *    a string, it passes through states that are not numbers (`""`, `"6."`,
 *    `"0."`, `"00"`), and it is the ONLY thing the caret is anchored to.
 *  - the **value** — the parsed number the rest of the app stores.
 *
 * Deriving the draft from the value on every keystroke destroys typing. The
 * scar this module was written for (2026-08-03, listing parcel dimensions):
 * the parcel form rendered `value={fromMmToLocale(parcel.length)}`, so typing
 * `6` (inches) became `6 × 25.4 = 152.4 → round → 152 mm` in state and came
 * straight back as `152 / 25.4 = 5.98`. The user typed 6 and watched the field
 * say 5.98. Typing `6.` was worse — `Number("6.")` is `6`, so the decimal point
 * was swallowed the instant it was typed and no fractional value could ever be
 * entered. Backspacing hit the same wall in reverse.
 *
 * The rule: **the draft is state, never a derivation.** It is re-synced from
 * the value only when the value changed for a reason other than this field's
 * own emit (external reset, form load, parent clamp) — see
 * `components/ui/numeric-input.tsx`.
 *
 * # Codecs
 *
 * A field's draft ⇄ value mapping is not always `Number()`. The parcel inputs
 * type in the seller's locale unit (in / cm / oz / kg) while state holds the
 * canonical unit (mm / grams). Rather than let the component convert — which
 * would put a lossy round-trip back between keystroke and render — the mapping
 * is injected as a `NumericFieldCodec`. The component then compares
 * `codec.parse(draft) === value` and, since that equality holds while typing,
 * never overwrites the draft.
 *
 * @see components/ui/numeric-input.tsx (the primitive that consumes this)
 * @see lib/shipping/parcel-locale-codec.ts (locale ⇄ canonical parcel codecs)
 */

/**
 * Draft ⇄ value mapping for one field.
 *
 * `parse` and `format` are NOT required to be exact inverses — a lossy
 * canonical store (integer mm) makes that impossible. What IS required:
 *
 *   `parse(format(v)) === v`   for every `v` the field can hold
 *
 * i.e. formatting a stored value must produce a draft that parses back to the
 * SAME stored value. Without this the field flickers on blur and re-sync.
 * `lib/shipping/parcel-locale-codec.ts` achieves it by searching for the
 * shortest decimal representation that survives the round trip.
 */
export interface NumericFieldCodec {
  /**
   * Draft string → stored value. Returns `undefined` when the draft carries
   * no number yet (`""`, `"."`, `"-"`) — that is a legitimate state the user
   * passes through, not an error.
   */
  parse: (draft: string) => number | undefined;
  /** Stored value → draft string, shown when the field syncs from outside. */
  format: (value: number) => string;
  /** Digits accepted after the decimal separator while typing. `0` = integers. */
  maxDecimals: number;
  /** Whether a leading `-` is accepted. Defaults to `false` for every current caller. */
  allowNegative?: boolean;
}

export interface SanitizeOptions {
  maxDecimals: number;
  allowNegative?: boolean;
}

/**
 * Filter a raw input event value down to characters this field accepts,
 * preserving every intermediate state a person types through.
 *
 * What it deliberately does NOT do:
 *  - parse to a number (that is `parse`, and it happens alongside, not instead)
 *  - drop a trailing `.` — `"6."` is how `6.5` is reached
 *  - drop a lone `"0"` or leading zeros — `"0.5"` starts as `"0"`
 *  - clamp to min/max — clamping mid-typing makes a field impossible to clear
 *    (typing `12` into a `min=10` field would rewrite `1` to `10`). Range is a
 *    validation concern, surfaced by the caller after the fact.
 *
 * Accepts `,` as a decimal separator (European keyboards emit it) and rewrites
 * it to `.` so a single canonical separator reaches `parse`.
 */
export function sanitizeNumericDraft(
  raw: string,
  { maxDecimals, allowNegative = false }: SanitizeOptions,
): string {
  const withDot = raw.replace(/,/g, ".");
  const negative = allowNegative && withDot.trimStart().startsWith("-");
  const digitsAndDots = withDot.replace(/[^0-9.]/g, "");

  // Keep only the first separator: "6.5.2" → "6.52". Splitting rather than
  // truncating means a stray second dot mid-string doesn't eat the tail the
  // user already typed.
  const [head, ...rest] = digitsAndDots.split(".");
  const hadSeparator = rest.length > 0;
  const tail = rest.join("");

  const sign = negative ? "-" : "";

  if (maxDecimals <= 0) {
    // Integer field: the separator and everything after it never existed.
    return `${sign}${head}`;
  }
  if (!hadSeparator) return `${sign}${head}`;
  return `${sign}${head}.${tail.slice(0, maxDecimals)}`;
}

/**
 * Parse a sanitised draft to a number.
 *
 * `undefined` means "no number here yet" and covers every partial state:
 * `""`, `"."`, `"-"`, `"-."`. Those are normal keystrokes, so callers must
 * treat `undefined` as "field cleared", not as a parse failure.
 */
export function parseNumericDraft(draft: string): number | undefined {
  if (draft === "") return undefined;
  const n = Number(draft);
  if (!Number.isFinite(n)) return undefined;
  // `Number(".")` is NaN (caught above) but `Number("-")` is NaN too, and
  // `Number("")` is 0 — the empty case is already handled, so anything
  // reaching here with no digit at all is a partial draft.
  if (!/[0-9]/.test(draft)) return undefined;
  return n;
}

/**
 * Format a number as a plain decimal draft, trimming the trailing zeros that
 * `toFixed` leaves behind (`6.50` → `6.5`, `6.00` → `6`). Those zeros are not
 * wrong, but they land in the field on blur and read as if the app retyped
 * the user's entry.
 */
export function formatNumericDraft(value: number, maxDecimals: number): string {
  if (!Number.isFinite(value)) return "";
  if (maxDecimals <= 0) return String(Math.round(value));
  const fixed = value.toFixed(maxDecimals);
  return fixed.includes(".") ? fixed.replace(/\.?0+$/, "") : fixed;
}

/**
 * Codec for a plain decimal field — price, rate, anything stored in the same
 * unit it is typed in.
 */
export function decimalCodec(maxDecimals = 2): NumericFieldCodec {
  return {
    parse: parseNumericDraft,
    format: (value) => formatNumericDraft(value, maxDecimals),
    maxDecimals,
  };
}

/**
 * Codec for a whole-number field — quantity, order counts, anything a
 * fractional value would be meaningless for.
 */
export function integerCodec(): NumericFieldCodec {
  return {
    parse: (draft) => {
      const n = parseNumericDraft(draft);
      return n === undefined ? undefined : Math.trunc(n);
    },
    format: (value) => String(Math.trunc(value)),
    maxDecimals: 0,
  };
}
